import { useState } from 'react'

import { ReportCreatePageQuery } from 'generated/resources/graphql'
import { isEqual } from 'lodash'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { ReportSchemaType } from 'modules/ai_reports/lib/types'

/*
 * NOTE: Schemas are difficult to manage.
 * Schema is necessary for different things, one is showing the appropriate
 * columns, another one is building the available filters.
 * The problem is that there's not just one schema but two: one is the schema
 * of the table, and another one is the schema of the query.
 * Both are important: for the available columns you need the query schema,
 * for the available filters you need a merge of both, and what's worse,
 * they might overlap.
 *
 * Currently we don't do any attempts to do the merge. Furthermore, there's a
 * problem in the Data Collection where changing the available filters will
 * not be reflected in the UI.
 */
export const useReportSchema = (
  irisTableColumns: NonNullable<
    ReportCreatePageQuery['iris']['factTable']
  >['columnsConnection']['nodes']
) => {
  const [schema, setSchema] = useState<ReportSchemaType>([])
  // Use functional setState + stable callback ref so we always compare against
  // the *latest* state. Previous `[schema]` deps caused a new handler reference
  // on every state change, which cascaded through consumers (useDataCollectionSource)
  // and contributed to an infinite re-render / re-fetch loop.
  const handleSchemaChanged = useUnsafeCallback((receivedSchema: ReportSchemaType) => {
    setSchema((prev) => (isEqual(receivedSchema, prev) ? prev : receivedSchema))
  }, [])
  useUnsafeEffect(() => {
    handleSchemaChanged(irisTableColumns.map((node) => node.schema))
  }, [irisTableColumns])

  return [schema, handleSchemaChanged] as const
}
